import {
  WhmBox,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
  SxProps,
} from "@securitize/reactjs-whm";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import { iconSmall } from "../../shared/iconThemes";
import { usePortfolioTopSectionLoadingContext } from "./PortfolioTopSectionContainer";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { Translate } from "../../Translation/Translate";
import { SnackbarContext } from "../../shared/Snackbar/SnackbarContext";
import { useContext, useEffect, useMemo } from "react";
import { currencyFormatter } from "../../../helpers/portfolio";
import ErrorSnackbar from "../../shared/ErrorSnackbar";
import StyledPortfolioCard from "./StyledCard";
import Performance from "./Assets/Performance";

export default function PortfolioValueCard({ sx }: { sx?: SxProps }) {
  const { showSnackbar } = useContext(SnackbarContext);
  const {
    portfolioBalancesQuery,
    portfolioPerformanceQuery,
    isContainerLoading,
  } = usePortfolioTopSectionLoadingContext();
  const balances = portfolioBalancesQuery;
  const assetPerformance = portfolioPerformanceQuery;
  // Use synchronized loading state
  const isLoading = isContainerLoading;

  const selectedBalance = useMemo(() => {
    const totalBalance = balances.data?.totalBalanceIn.data;
    const currency = balances.data?.displayCurrency;
    return totalBalance?.find((b) => b.currency === currency);
  }, [balances.data]);

  const checkBalancesError = useMemo(() => {
    const { cashBalances, investmentsBalanceIn } = balances.data ?? {};
    return {
      cashBalanceStatusError: cashBalances?.status === "ERROR",
      investmentsStatusError: investmentsBalanceIn?.status === "ERROR",
    };
  }, [balances.data]);

  const errorMessage = useMemo(() => {
    if (checkBalancesError.cashBalanceStatusError)
      return translationKeys.PORTFOLIO_ERROR;
    if (checkBalancesError.investmentsStatusError)
      return translationKeys.PORTFOLIO_ERROR_INVESTMENTS;
    return null;
  }, [checkBalancesError]);

  const currencyFormat = useMemo(
    () =>
      currencyFormatter({
        currency: balances.data?.displayCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [balances.data?.displayCurrency],
  );

  useEffect(() => {
    if (balances.isError) {
      showSnackbar({
        severity: "error",
        message: <ErrorSnackbar error={balances.error} />,
      });
    }
  }, [balances.error, balances.isError, showSnackbar]);

  return (
    <StyledPortfolioCard
      sx={{
        ...sx,
        "& > div": {
          height: "100%",
        },
      }}
    >
      <WhmStack
        gap="16px"
        sx={{ height: "100%", justifyContent: "space-between" }}
      >
        <>
          {isLoading || balances.isError ? (
            <WhmBox
              id="portfolio-value-skeleton"
              data-test-id="potrfolio-value-skeleton"
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <WhmSkeleton
                variant="rounded"
                sx={{ marginBottom: "6px" }}
                width={160}
                height={32}
              />
              <WhmStack gap="8px">
                <WhmSkeleton variant="rounded" width={310} height={42} />
                <WhmSkeleton variant="rounded" width={130} height={20} />
              </WhmStack>
            </WhmBox>
          ) : (
            <>
              <WhmBox
                sx={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "baseline",
                }}
              >
                <WhmTypography variant="h4">
                  <Translate translationKey={translationKeys.PORTFOLIO_VALUE} />
                </WhmTypography>
                <WhmTooltip
                  placement="top-start"
                  data-test-id="portfolio-value-tooltip"
                  title={
                    <Translate
                      translationKey={translationKeys.PORTFOLIO_TOOLTIP_TEXT}
                    />
                  }
                >
                  <InfoOutlined
                    style={iconSmall}
                    fill={whmTheme.palette.action.active}
                  />
                </WhmTooltip>
              </WhmBox>
              <WhmBox>
                <WhmTypography variant="h1">
                  {selectedBalance &&
                    currencyFormat.format(selectedBalance.total)}
                </WhmTypography>
                {assetPerformance.isSuccess && (
                  <WhmStack direction="row">
                    <Performance
                      value={assetPerformance.data.totalValue}
                      variant="body2"
                    >
                      {currencyFormat.format(
                        Math.abs(assetPerformance.data.totalValue),
                      )}
                    </Performance>
                    <WhmTypography
                      sx={{
                        color: whmTheme.palette.text.secondary,
                        paddingLeft: "4px",
                      }}
                      variant="body2"
                    >
                      <Translate
                        translationKey={
                          translationKeys.PORTFOLIO_PERFORMANCE_TIME
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                )}
              </WhmBox>
            </>
          )}
        </>
        {errorMessage && (
          <WhmTypography
            sx={{
              color: whmTheme.palette.error.main,
              paddingTop: "16px",
            }}
            variant="body2"
          >
            <Translate translationKey={errorMessage} />
          </WhmTypography>
        )}
      </WhmStack>
    </StyledPortfolioCard>
  );
}
